import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Icon,
  Loading,
  Pane,
  PaneFooter,
  PaneMenu,
  Row,
  Col,
} from '@folio/stripes-components';
import { FormattedMessage } from 'react-intl';
import ImageUploadCard from './components/ImageUploadCard';
import ExtractionResultsCard from './components/ExtractionResultsCard';
import ExtractionTypeSelector from './components/ExtractionTypeSelector';
import VisionModelSelector from './components/VisionModelSelector';
import { mapExtractedDataToForm } from '../../utils/extractedDataMapper';

function ImageExtractionPanel({
  onClose,
  onExtractComplete,
  controlledVocab,
  currentTemplate,
  extractionResults,
  setExtractionResults,
  showResults,
  setShowResults,
  selectedImage,
  setSelectedImage,
  imagePreview,
  setImagePreview,
  extractionType,
  setExtractionType,
  selectedModel,
  setSelectedModel,
  isExtracting,
  setIsExtracting,
  extractionProgress,
  setExtractionProgress,
  serviceError,
  setServiceError,
}) {
  const handleImageSelect = (file, preview) => {
    setSelectedImage(file);
    setImagePreview(preview);
    setServiceError(null);
  };

  const handleImageChange = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setShowResults(false);
    setExtractionResults(null);
    setServiceError(null);
  };

  const handleExtract = async () => {
    if (!selectedImage) return;

    setIsExtracting(true);
    setExtractionProgress(
      <FormattedMessage
        id="ui-plugin-inventory-templates.imageExtraction.checkingService"
        defaultMessage="Checking service availability..."
      />,
    );
    setServiceError(null);
    setShowResults(false);

    try {
      const visionOcrService = (await import('../../services/visionOcrService'))
        .default;

      const isAvailable = await visionOcrService.isServiceAvailable();
      if (!isAvailable) {
        throw new Error(
          'Text extraction service is unavailable. Make sure the vision model service is running',
        );
      }

      const ocrResult = await visionOcrService.extractArabicText(
        selectedImage,
        (progress) => setExtractionProgress(progress),
        { extractionType, selectedModel },
      );

      if (!ocrResult || !ocrResult.success) {
        throw new Error('Failed to extract text from image');
      }

      setExtractionResults({
        extractedData: ocrResult.data,
        ocrResult,
        extractionType,
      });
      setShowResults(true);
    } catch (error) {
      setServiceError(error.message);
    } finally {
      setIsExtracting(false);
      setExtractionProgress('');
    }
  };

  const handleApplyResults = () => {
    if (extractionResults && controlledVocab && currentTemplate) {
      const mappedData = mapExtractedDataToForm(
        extractionResults.extractedData,
        controlledVocab,
        currentTemplate,
      );
      onExtractComplete(mappedData);
    }
  };

  const renderLastMenu = () => (
    <PaneMenu>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '0 8px',
        }}
      >
        <VisionModelSelector
          selectedModel={selectedModel}
          onModelChange={setSelectedModel}
          disabled={isExtracting}
        />
        {/* <ExtractionTypeSelector
          extractionType={extractionType}
          onExtractionTypeChange={setExtractionType}
          disabled={isExtracting}
        /> */}
      </div>
    </PaneMenu>
  );

  const renderFooter = () => (
    <PaneFooter
      renderEnd={
        showResults && extractionResults ? (
          <>
            <Button
              buttonStyle="default"
              onClick={handleExtract}
              disabled={isExtracting}
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.imageExtraction.retry"
                defaultMessage="Try Again"
              />
            </Button>
            <Button buttonStyle="primary" onClick={handleApplyResults}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.imageExtraction.applyToForm"
                defaultMessage="Apply to Form"
              />
            </Button>
          </>
        ) : (
          <Button
            buttonStyle="primary"
            onClick={handleExtract}
            disabled={!selectedImage || isExtracting}
          >
            {isExtracting ? (
              <>
                <Icon icon="spinner-ellipsis" />
                <FormattedMessage
                  id="ui-plugin-inventory-templates.imageExtraction.extracting"
                  defaultMessage="Extracting..."
                />
              </>
            ) : (
              <FormattedMessage
                id="ui-plugin-inventory-templates.imageExtraction.extract"
                defaultMessage="Extract Information"
              />
            )}
          </Button>
        )
      }
    />
  );

  return (
    <Pane
      id="image-extraction-panel"
      defaultWidth="40%"
      paneTitle={
        <FormattedMessage
          id="ui-plugin-inventory-templates.imageExtraction.modalTitle"
          defaultMessage="Extract Information from Image"
        />
      }
      dismissible
      onClose={onClose}
      lastMenu={renderLastMenu()}
      footer={renderFooter()}
    >
      <Row>
        <Col xs={12}>
          <ImageUploadCard
            selectedImage={selectedImage}
            imagePreview={imagePreview}
            onImageSelect={handleImageSelect}
            onImageChange={handleImageChange}
          />
        </Col>
      </Row>

      {selectedImage && isExtracting && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            textAlign: 'center',
            marginTop: '30px',
          }}
        >
          <Loading size="large" />
          {extractionProgress || (
            <FormattedMessage
              id="ui-plugin-inventory-templates.imageExtraction.processingMessage"
              defaultMessage="Processing image and extracting information..."
            />
          )}
        </div>
      )}

      {selectedImage && serviceError && (
        <div
          style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            fontSize: '14px',
          }}
        >
          <strong>
            <FormattedMessage
              id="ui-plugin-inventory-templates.imageExtraction.error"
              defaultMessage="Error:"
            />
          </strong>{' '}
          {serviceError}
        </div>
      )}

      {selectedImage && showResults && extractionResults && !isExtracting && (
        <Row style={{ marginTop: '16px' }}>
          <Col xs={12}>
            <ExtractionResultsCard
              extractionResults={extractionResults}
              onRetry={handleExtract}
            />
          </Col>
        </Row>
      )}
    </Pane>
  );
}

ImageExtractionPanel.propTypes = {
  onClose: PropTypes.func.isRequired,
  onExtractComplete: PropTypes.func.isRequired,
  controlledVocab: PropTypes.object,
  currentTemplate: PropTypes.object,
  extractionResults: PropTypes.object,
  setExtractionResults: PropTypes.func.isRequired,
  showResults: PropTypes.bool.isRequired,
  setShowResults: PropTypes.func.isRequired,
  selectedImage: PropTypes.object,
  setSelectedImage: PropTypes.func.isRequired,
  imagePreview: PropTypes.string,
  setImagePreview: PropTypes.func.isRequired,
  extractionType: PropTypes.string.isRequired,
  setExtractionType: PropTypes.func.isRequired,
  selectedModel: PropTypes.string.isRequired,
  setSelectedModel: PropTypes.func.isRequired,
  isExtracting: PropTypes.bool.isRequired,
  setIsExtracting: PropTypes.func.isRequired,
  extractionProgress: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  setExtractionProgress: PropTypes.func.isRequired,
  serviceError: PropTypes.string,
  setServiceError: PropTypes.func.isRequired,
};

export default ImageExtractionPanel;
